import { Marker } from 'react-leaflet';
import { MarkerCluster } from '../utils/clustering';
import { createClusteredFriendIcon } from '../utils';
import { FriendMarker } from './FriendMarker';
import { TemporaryLocationWithFriend } from '../../../types';

interface ClusteredMarkerProps {
  cluster: MarkerCluster;
  mapZoom: number;
  onZoomToCluster: (center: [number, number], zoom: number) => void;
  getPhotoUrl: (photo?: string) => string | null;
  highlightedFriendId: number | null;
  highlightedTempLocationId: number | null;
  onContactClick: (id: number) => void;
  onDirectionsClick: (info: {
    lat: number;
    lng: number;
    name?: string;
    address?: string;
    profilePhoto?: string;
    firstName?: string;
    lastName?: string;
    friendId?: number;
  }) => void;
  loadingFriend: boolean;
  t: (key: string) => string;
  tempLocations?: TemporaryLocationWithFriend[];
  onTempLocationClick?: (tempLocation: TemporaryLocationWithFriend) => void;
  hiddenFriendIds?: number[]; // Friends to hide (e.g., route points rendered by RoutePolyline)
}

/**
 * Component that renders either a single friend marker or a clustered marker
 * If clustered (>1 friend), clicking zooms in to separate the markers
 */
export const ClusteredMarker: React.FC<ClusteredMarkerProps> = ({
  cluster,
  mapZoom,
  onZoomToCluster,
  getPhotoUrl,
  highlightedFriendId,
  highlightedTempLocationId,
  onContactClick,
  onDirectionsClick,
  loadingFriend,
  t,
  tempLocations,
  onTempLocationClick,
  hiddenFriendIds = [],
}) => {
  // Filter out hidden friends (route points rendered by RoutePolyline)
  const visibleFriends = hiddenFriendIds.length > 0
    ? cluster.friends.filter(f => !hiddenFriendIds.includes(f.id))
    : cluster.friends;

  // If no friends visible after filtering, don't render
  if (visibleFriends.length === 0) {
    return null;
  }

  // If only one friend visible, render normal FriendMarker
  if (visibleFriends.length === 1) {
    const friend = visibleFriends[0];
    // Check if popup should open based on either friend ID or temp location ID
    const shouldOpenPopup = highlightedFriendId === friend.id || highlightedTempLocationId === friend.id;
    return (
      <FriendMarker
        key={friend.id}
        friend={friend}
        shouldOpenPopup={shouldOpenPopup}
        onContactClick={onContactClick}
        onDirectionsClick={onDirectionsClick}
        getPhotoUrl={getPhotoUrl}
        loadingFriend={loadingFriend}
        t={t}
        tempLocations={tempLocations}
        onTempLocationClick={onTempLocationClick}
      />
    );
  }

  // Multiple friends - render clustered marker
  const clusteredIcon = createClusteredFriendIcon(visibleFriends, getPhotoUrl);

  // Calculate zoom level to separate markers
  const calculateZoomForCluster = (): number => {
    // Find the maximum distance between any two points
    let maxDistance = 0;
    for (let i = 0; i < visibleFriends.length; i++) {
      for (let j = i + 1; j < visibleFriends.length; j++) {
        const f1 = visibleFriends[i];
        const f2 = visibleFriends[j];
        if (!f1.latitude || !f1.longitude || !f2.latitude || !f2.longitude) continue;

        const latDiff = Math.abs(f1.latitude - f2.latitude);
        const lngDiff = Math.abs(f1.longitude - f2.longitude);
        const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
        maxDistance = Math.max(maxDistance, distance);
      }
    }

    // Determine zoom level based on distance
    if (maxDistance < 0.001) {
      return Math.min(mapZoom + 4, 18); // Very close - zoom in a lot
    } else if (maxDistance < 0.01) {
      return Math.min(mapZoom + 3, 18); // Close - zoom in moderately
    } else if (maxDistance < 0.1) {
      return Math.min(mapZoom + 2, 18); // Medium distance
    } else {
      return Math.min(mapZoom + 1, 18); // Far apart
    }
  };

  const handleClusterClick = () => {
    const newZoom = calculateZoomForCluster();
    onZoomToCluster(cluster.center, newZoom);
  };

  return (
    <Marker
      position={cluster.center}
      icon={clusteredIcon}
      eventHandlers={{
        click: handleClusterClick,
      }}
    />
  );
};
